import React, { useEffect, useMemo, useState } from "react";
import { Activity, Cpu, HardDrive, MemoryStick, Wifi } from "lucide-react";
import { useWindows } from "../WindowManager";
import "./utility.css";

const SYSTEM_PROCESSES = [
  { name: "System", type: "Windows process", base: 0.4, mem: 128 },
  { name: "Windows Explorer", type: "Windows process", base: 1.2, mem: 96 },
  { name: "Desktop Window Manager", type: "Windows process", base: 2.1, mem: 142 },
  { name: "Antimalware Service Executable", type: "Windows process", base: 0.8, mem: 210 },
  { name: "Service Host: Network Service", type: "Windows process", base: 0.2, mem: 38 },
];

const jitter = (base) => Math.max(0, base + (Math.random() - 0.5) * base * 0.9);

export default function TaskManagerApp() {
  const { windows, apps, close, focus } = useWindows();
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  const rows = useMemo(() => {
    const appRows = windows.map((w) => {
      const app = apps.find((a) => a.id === w.appId);
      return {
        id: w.id,
        name: app?.title || w.appId,
        type: "App",
        cpu: jitter(1.6),
        mem: 64 + ((w.appId.length * 37) % 180) + Math.random() * 12,
        disk: jitter(0.4),
        network: jitter(0.1),
        closable: true,
      };
    });
    const sysRows = SYSTEM_PROCESSES.map((p) => ({
      id: p.name,
      name: p.name,
      type: p.type,
      cpu: jitter(p.base),
      mem: p.mem + Math.random() * 20,
      disk: jitter(0.2),
      network: jitter(0.05),
    }));
    return [...appRows, ...sysRows];
    // eslint-disable-next-line
  }, [windows, apps, tick]);

  const totals = rows.reduce(
    (acc, r) => ({
      cpu: acc.cpu + r.cpu,
      mem: acc.mem + r.mem,
      disk: acc.disk + r.disk,
      network: acc.network + r.network,
    }),
    { cpu: 0, mem: 0, disk: 0, network: 0 }
  );

  const memPct = Math.min(99, Math.round((totals.mem / 16384) * 100 + 32));
  const cpuPct = Math.min(99, Math.round(totals.cpu + 4));

  const heat = (v, max) => {
    const ratio = Math.min(1, v / max);
    if (ratio > 0.66) return "tm-heat--high";
    if (ratio > 0.33) return "tm-heat--mid";
    return "";
  };

  return (
    <div className="tm">
      <div className="tm-summary">
        {[
          { icon: Cpu, label: "CPU", value: `${cpuPct}%`, sub: "Web Runtime x64" },
          { icon: MemoryStick, label: "Memory", value: `${memPct}%`, sub: "of 16.0 GB" },
          { icon: HardDrive, label: "Disk", value: `${totals.disk.toFixed(1)}%`, sub: "C: SSD" },
          { icon: Wifi, label: "Network", value: `${totals.network.toFixed(1)} Mbps`, sub: "Wi-Fi" },
        ].map((card) => (
          <div className="tm-card" key={card.label}>
            <span className="tm-card__icon">
              <card.icon size={16} />
            </span>
            <div className="tm-card__body">
              <div className="tm-card__label">{card.label}</div>
              <div className="tm-card__value">{card.value}</div>
              <div className="tm-card__sub">{card.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="tm-tablewrap win-scroll">
        <table className="tm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Disk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className={selected === r.id ? "tm-row--on" : ""}
                onClick={() => {
                  setSelected(r.id);
                  if (r.closable) focus(r.id);
                }}
              >
                <td>
                  <span className="tm-name">
                    <Activity size={13} />
                    {r.name}
                  </span>
                  <span className="tm-type">{r.type}</span>
                </td>
                <td>Running</td>
                <td className={heat(r.cpu, 3)}>{r.cpu.toFixed(1)}%</td>
                <td className={heat(r.mem, 260)}>{Math.round(r.mem)} MB</td>
                <td>{r.disk.toFixed(1)} MB/s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tm-foot">
        <span>{rows.length} processes</span>
        <button
          className="win-btn"
          disabled={!rows.find((r) => r.id === selected)?.closable}
          onClick={() => {
            close(selected);
            setSelected(null);
          }}
        >
          End task
        </button>
      </div>
    </div>
  );
}
